import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment';
import { useUserStore } from '../../store/useUserStore';
import Url from '../../data/url';
import { BOTTOM_NAVI_V2, COMMUNITY_QNA_TYPE } from '../../data/define';
import { useQueryClient } from '@tanstack/react-query';
import {
  useKidDetailQuery,
  useKidReportDetailQuery,
  useReportsCompareDataQuery,
  useReportsTotalCompareDataQuery,
  kidsQueries,
} from '../../queries';
import IcDotsBtn from '../../assets/image/report/ic_dots_btn.svg';
import FullHeightContainer from '../../component/common/FullHeightContainer';
import TopHeaderWithPrev from '../../component/common/TopHeaderWithPrev';
import Divider from '../../component/common/Divider';
import Header from '../../component/report/Header';
import VideoList from '../../component/report/VideoList';
import MultiIntelligenceBar from '../../component/report/MultiIntelligenceBar';
import IntelligenceRankTriange from '../../component/report/IntelligenceRankTriange';
import StrengthWeaknessRadar from '../../component/report/StrengthWeaknessRadar';
import NeedMoreIntelligence from '../../component/report/NeedMoreIntelligence';
import ReportSummary from '../../component/report/ReportSummary';
import ReportCore from '../../component/report/ReportCore';
import QuestionAnalysis from '../../component/report/QuestionAnalysis';
import QuestionGrowth from '../../component/report/QuestionGrowth';
import QuestionAccumulated from '../../component/report/QuestionAccumulated';
import ChangeSummary from '../../component/report/ChangeSummary';
import ChangeFlowPreNextChart from '../../component/report/ChangeFlowPreNextChart';
import ChangAccumulatedSummary from '../../component/report/ChangeAccumulatedSummary';
import ChangeFlowCumulativeChart from '../../component/report/ChangeFlowCumulativeChart';
import ChangeComparisonToggle from '../../component/report/ChangeComprisonToggle';
import MoreDialog from '../../component/report/MoreDialog';
import OneBtnAlert from '../../component/common/OneBtnAlert';
import BottomNavigationV2 from '../../component/common/BottomNavigationV2';
import { useTranslation } from '../../hook/useTranslation';

const TabCase = {
  Result: 1,
  Change: 2,
};
const ComparisonType = {
  Previous: 1,
  Cumulative: 2,
};

const THRESHOLD = 150;

function ReportPage() {
  const { t } = useTranslation();
  const user = useUserStore();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const reportIdFromQuery = queryParams.get('reportId');
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const [tab, setTab] = useState(TabCase.Result);
  const [needNewVideoOpen, setNeedNewVideoOpen] = useState(false);
  const [moreDialogOpen, setMoreDialogOpen] = useState(false);
  const [changeToggle, setChangeToggle] = useState(ComparisonType.Previous);
  const [reports, setReports] = useState<any[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
  const [lastReportId, setLastReportId] = useState<number | null>(null);
  const [isOpenToggleBtn, setIsOpenToggleBtn] = useState(false);

  const queryClient = useQueryClient();

  const { data: kidDetail } = useKidDetailQuery(user?.mainKid?.id as number, {
    enabled: !!user?.mainKid?.id,
  });

  const { data: reportDetail, refetch: getReportDetail } = useKidReportDetailQuery(
    selectedReportId as number,
    { enabled: !!selectedReportId }
  );

  const { data: compareTwoResult } = useReportsCompareDataQuery(
    {
      baseReportId: lastReportId,
      targetReportId: selectedReportId,
    },
    {
      enabled: changeToggle === ComparisonType.Previous && !!lastReportId && !!selectedReportId,
    }
  );

  const { data: compareTotalResult } = useReportsTotalCompareDataQuery(
    {
      kidId: user.mainKid?.id,
      reportId: selectedReportId,
    },
    {
      enabled:
        changeToggle === ComparisonType.Cumulative && !!selectedReportId && !!user.mainKid?.id,
    }
  );

  const loadReport = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: kidsQueries.all });
  }, [queryClient]);

  useEffect(() => {
    if (!kidDetail) return;
    if (kidDetail.reports.length < 1) {
      navigate(Url.Home, { replace: true });
      return;
    }
    let filteredReports: any[] = [];
    if (tab === TabCase.Result) {
      const chosenReports = kidDetail.reports || [];
      filteredReports = chosenReports;
      const latestReport =
        chosenReports?.length > 0
          ? chosenReports.reduce((latest: any, current: any) => {
              const latestDate = moment(latest.updatedAt);
              const currentDate = moment(current.updatedAt);
              return currentDate.isAfter(latestDate) ? current : latest;
            })
          : null;
      setSelectedReportId(reportIdFromQuery ? Number(reportIdFromQuery) : latestReport?.id);
    } else {
      const chosenReports = kidDetail.reports?.filter((r: any) => r.isSuccess) || [];
      filteredReports = chosenReports;
      const chosenSuccessReport = chosenReports?.find((r: any) => r.isSuccess);
      const targetId = reportIdFromQuery ? Number(reportIdFromQuery) : chosenSuccessReport?.id;
      setSelectedReportId(targetId);

      const indexOfChosenSuccess = chosenReports.findIndex((r: any) => r.id === targetId);
      if (indexOfChosenSuccess !== -1) {
        if (indexOfChosenSuccess === chosenReports.length - 1) {
          setLastReportId(chosenReports[chosenReports.length - 1]?.id);
        } else {
          setLastReportId(chosenReports[indexOfChosenSuccess + 1]?.id);
        }
      }
    }
    setReports(filteredReports);
  }, [kidDetail, tab, reportIdFromQuery, navigate]);

  // 상단 네비게이터 색상 컨트롤
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const onScroll = () => {
      const y = container.scrollTop;
      setScrolled(y > THRESHOLD);
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      container.removeEventListener('scroll', onScroll);
    };
  }, []);

  // 영상 12개이나 포착지능 부족시
  useEffect(() => {
    if (reportDetail) {
      if (reportDetail?.mediaSummary?.length === 12 && reportDetail?.SecondAnalysis?.length < 8) {
        setNeedNewVideoOpen(true);
      }
    }
  }, [reportDetail]);

  return (
    <FullHeightContainer>
      <TopHeaderWithPrev
        title=""
        style={{
          backgroundColor: scrolled ? '#fff' : '#d4f4f5',
          transition: 'background-color 0.3s ease-in-out',
        }}
        onClickPrev={() => navigate(-1)}
      />
      <button
        type="button"
        className="absolute right-6 top-2.5 z-10"
        onClick={() => setMoreDialogOpen(true)}
      >
        <img src={IcDotsBtn} alt="dots-button" />
      </button>
      <div className="pageInner" ref={scrollRef} style={{ paddingBottom: '100px' }}>
        <Header
          tab={tab}
          setTab={setTab}
          TabCase={TabCase}
          changeToggle={changeToggle}
          reports={reports}
          selectedReportId={selectedReportId}
          setSelectedReportId={setSelectedReportId}
          lastReportId={lastReportId}
          setLastReportId={setLastReportId}
          shouldShowMoreVideo={
            reportDetail &&
            !reports?.find((r) => r.id === selectedReportId)?.isSuccess &&
            reportDetail?.SecondAnalysis?.length >= 4 &&
            reportDetail?.SecondAnalysis?.length < 8
          }
          shouldShowLackVideo={
            reportDetail &&
            !reports?.find((r) => r.id === selectedReportId)?.isSuccess &&
            reportDetail?.SecondAnalysis?.length < 4
          }
        />
        <Divider />
        {tab === TabCase.Result && (
          <>
            <VideoList
              videos={reportDetail?.mediaSummary ?? []}
              intelligences={reportDetail?.SecondAnalysis ?? []}
              selectedReportId={selectedReportId}
              reload={getReportDetail}
              reloadReports={loadReport}
              isSuccess={reports?.find((r) => r.id === selectedReportId)?.isSuccess}
              setIsOpenToggleBtn={setIsOpenToggleBtn}
            />
            <Divider />
            <MultiIntelligenceBar
              result={
                reportDetail?.SecondAnalysis?.map((item: any) => ({
                  id: item.intelligence,
                  score: item.score,
                })) ?? []
              }
            />
            <Divider />
            <IntelligenceRankTriange
              result={
                reportDetail?.SecondAnalysis?.map((item: any) => ({
                  id: item.intelligence,
                  percentile: item.percentile,
                })) ?? []
              }
            />
            <Divider />
            <StrengthWeaknessRadar
              result={
                reportDetail?.SecondAnalysis?.map((item: any) => ({
                  id: item.intelligence,
                  score: item.score,
                })) ?? []
              }
            />
            <Divider />
            <ReportSummary
              result={
                reportDetail?.SecondAnalysis?.map((item: any) => ({
                  id: item.intelligence,
                  score: item.score,
                })) ?? []
              }
            />
            <Divider />
            <ReportCore
              result={reportDetail?.SecondAnalysis}
              thumbnails={reportDetail?.thumbnail?.report_thumbnails ?? []}
            />
            <NeedMoreIntelligence
              intelligences={reportDetail?.SecondAnalysis ?? []}
              isSuccess={reports?.find((r) => r.id === selectedReportId)?.isSuccess}
              setIsOpenToggleBtn={setIsOpenToggleBtn}
            />
            <Divider />
            <QuestionAnalysis
              selectedReportId={selectedReportId}
              questions={reportDetail?.questions?.filter(
                (q: any) => q.questionType === COMMUNITY_QNA_TYPE.ANALYSIS
              )}
              reload={getReportDetail}
            />
          </>
        )}
        {tab === TabCase.Change && (
          <div className="relative w-full pb-[50px]">
            {changeToggle === ComparisonType.Previous ? (
              <>
                <ChangeSummary
                  ComparisonType={ComparisonType}
                  changeToggle={changeToggle}
                  compareResult={
                    changeToggle === ComparisonType.Previous ? compareTwoResult : compareTotalResult
                  }
                  selectedReportId={selectedReportId}
                />
                <Divider />
                <ChangeFlowPreNextChart
                  changeToggle={changeToggle}
                  compareResult={compareTwoResult}
                  lastDate={
                    moment(reports?.find((r) => r.id === lastReportId)?.updatedAt)?.format(
                      t('common.date_format')
                    ) || ''
                  }
                  currentDate={
                    moment(reports?.find((r) => r.id === selectedReportId)?.updatedAt)?.format(
                      t('common.date_format')
                    ) || ''
                  }
                  result={
                    reportDetail?.SecondAnalysis?.map((item: any) => ({
                      id: item.intelligence,
                      percentile: item.percentile,
                    })) ?? []
                  }
                />
                <Divider />
                <QuestionGrowth
                  selectedReportId={selectedReportId}
                  lastReportId={lastReportId}
                  questions={reportDetail?.questions?.filter(
                    (q: any) => q.questionType === COMMUNITY_QNA_TYPE.GROWTH
                  )}
                  reload={getReportDetail}
                />
              </>
            ) : (
              <>
                <ChangAccumulatedSummary compareResult={compareTotalResult} />
                <Divider />
                <ChangeFlowCumulativeChart
                  changeToggle={changeToggle}
                  compareResult={compareTotalResult}
                />
                <Divider />
                <QuestionAccumulated
                  selectedReportId={selectedReportId}
                  question={reportDetail?.questions?.find(
                    (q: any) => q.questionType === COMMUNITY_QNA_TYPE.ACCUMULATED
                  )}
                  reload={getReportDetail}
                />
              </>
            )}
            <ChangeComparisonToggle
              ComparisonType={ComparisonType}
              changeToggle={changeToggle}
              setChangeToggle={setChangeToggle}
            />
          </div>
        )}
        <MoreDialog
          mainKidId={user?.mainKid?.id}
          open={moreDialogOpen}
          onClose={() => setMoreDialogOpen(false)}
          reloadReports={loadReport}
          selectedReportId={selectedReportId}
          setIsOpenToggleBtn={setIsOpenToggleBtn}
        />
      </div>
      <OneBtnAlert
        open={needNewVideoOpen}
        onClose={() => setNeedNewVideoOpen(false)}
        title={t('report.alert.lack_intelligence_title')}
        description={t('report.alert.lack_intelligence_desc')}
        btnText={t('common.confirm')}
        onClick={() => setNeedNewVideoOpen(false)}
      />
      <BottomNavigationV2
        focusTab={BOTTOM_NAVI_V2.REPORT}
        isOpenToggleBtn={isOpenToggleBtn}
        setIsOpenToggleBtn={setIsOpenToggleBtn}
        currentReportVideoCount={reportDetail?.mediaSummary?.length}
      />
    </FullHeightContainer>
  );
}

export default ReportPage;
